import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { RollText } from '@/components/site/roll-text'
import { footerLinks, socialLinks } from '@/constants/site'
import { media } from '@/constants/media'

export const Footer: FC = (): ReactNode => {
    return (
        <footer className='relative z-[1] flex w-full justify-center border-t border-border bg-background'>
            <div className='flex w-full max-w-site flex-col overflow-clip p-4 md:p-0'>
                <div className='flex flex-col gap-5 md:flex-row md:items-end md:justify-center md:gap-10 md:p-4 lg:px-6 lg:pb-0 lg:pt-10'>
                    <div className='order-2 w-full py-5 md:order-1 md:max-w-[36%] md:flex-1 md:pb-4 md:pr-5 md:pt-5 lg:pb-5'>
                        <div className='relative aspect-[1.5] w-full overflow-hidden bg-coal'>
                            <img
                                src={media.footerImage}
                                alt="A minimal computer setup on a wooden desk in natural light"
                                loading='lazy'
                                decoding='async'
                                className='animate-drift absolute inset-0 size-full object-cover'
                            />
                        </div>
                    </div>

                    <div className='order-1 flex w-full items-end justify-between gap-4 md:order-2 md:flex-1 md:justify-end md:gap-20 lg:gap-[120px]'>
                        <nav aria-label="Elsewhere">
                            <ul className='flex flex-1 flex-col items-start pb-[10px] pt-6 md:items-end md:pb-4 lg:pb-[10px]'>
                                {socialLinks.map((link) => {
                                    const external = link.href.startsWith('http')

                                    return (
                                        <li key={link.href}>
                                            <a
                                                href={link.href}
                                                target={external ? '_blank' : undefined}
                                                rel={external ? 'noopener noreferrer' : undefined}
                                                className='roll-trigger group/small t-link relative inline-flex overflow-clip p-1 text-foreground'
                                            >
                                                <RollText text={link.label} />

                                                <span
                                                    aria-hidden='true'
                                                    className='absolute inset-x-1 bottom-px z-[1] h-px origin-left scale-x-0 bg-foreground transition-transform duration-base ease-studio group-hover/small:scale-x-100 group-focus-visible/small:scale-x-100'
                                                />
                                            </a>
                                        </li>
                                    )
                                })}
                            </ul>
                        </nav>

                        <nav aria-label="Footer">
                            <ul className='dim-group flex w-min flex-col items-end pb-3 lg:pb-0'>
                                {footerLinks.map((link) => (
                                    <li key={link.href} className='dim-item'>
                                        <Link
                                            href={link.href}
                                            className='roll-trigger t-footer block text-right text-foreground'
                                        >
                                            <RollText text={link.label} stagger={0.0125} />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>

                <div className='flex items-start justify-between gap-4 md:items-end md:p-4 lg:px-6 lg:pb-8 lg:pt-2'>
                    <p className='t-caps flex items-center gap-2 text-[20px] text-foreground'>
                        <span>
                            &#9426; Zaki Hatta 2026,
                        </span>

                        <Link
                            href='/legal/privacy-policy'
                            className='underline decoration-1 underline-offset-[3px] transition-opacity duration-fast ease-studio hover:opacity-70'
                        >
                            Legal
                        </Link>
                    </p>

                    <p className='flex items-center gap-1 text-right'>
                        <span className='t-caps text-[20px] text-foreground'>
                            Built with care in
                        </span>

                        <span className='font-serif text-[18px] italic leading-[0.9] text-umber'>
                            Padang
                        </span>
                    </p>
                </div>
            </div>
        </footer>
    )
}
